import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, ShoppingCart, Clock, Users, ChevronRight, Plus, Check,
  RotateCcw, Store, RefreshCw, CalendarDays, Star, ChefHat, Tag, TrendingDown,
  Bookmark, BookmarkCheck, ThumbsUp, ThumbsDown, Shuffle
} from 'lucide-react';
import { useAppStore } from '@/hooks/useAppStore';
import { supabase } from '@/lib/supabase';
import { ROUTE_PATHS, budgetLabel, budgetColor } from '@/lib/index';
import { staggerContainer, staggerItem } from '@/lib/motion';
import FlagRecipeButton from '@/components/FlagRecipeButton';
import { useSyncedRecipes } from '@/hooks/useSyncedRecipes';
import { useChefGPT } from '@/hooks/useChefGPT';
import SwapMealDrawer from '@/components/SwapMealDrawer';

// ── Swap toast state ─────────────────────────────────────────────────────────
interface SwapTarget { mealId: string; mealObj: import('@/lib/index').Meal; }

function sp(v: unknown): number {
  if (typeof v === 'number') return isNaN(v) ? 0 : Math.max(0, v);
  const p = parseFloat(String(v ?? '').replace(/[$,\s]/g, ''));
  return isNaN(p) ? 0 : Math.max(0, p);
}

function mealCostCalc(meal: { ingredients?: { price: unknown }[]; total_cost?: unknown }) {
  return meal.ingredients?.reduce((s, i) => s + sp(i.price), 0) ?? sp(meal.total_cost);
}

// ── Protein image map ─────────────────────────────────────────────────────────
const PROTEIN_IMAGES: [RegExp, string][] = [
  [/salmon|trout|halibut|sablefish|arctic char/i,  '/proteins/salmon_1.png'],
  [/shrimp|prawn|scallop|lobster|crab/i,           '/proteins/shrimp_1.png'],
  [/cod|tilapia|haddock|pollock|basa|sole|snapper|mahi|pickerel|perch|tuna|fish\b/i, '/proteins/fish_1.png'],
  [/ground beef|ground turkey|ground pork|mince/i, '/proteins/ground_beef_1.png'],
  [/steak|sirloin|rib.?eye|strip loin|tenderloin|brisket|short rib|oxtail|beef roast|beef chuck/i, '/proteins/beef_1.png'],
  [/\bbeef\b|veal/i,                               '/proteins/beef_1.png'],
  [/chicken/i,                                     '/proteins/chicken_1.png'],
  [/turkey/i,                                      '/proteins/turkey_1.png'],
  [/lamb|mutton/i,                                 '/proteins/lamb_1.png'],
  [/pork|ham\b|bacon|sausage|bratwurst|chorizo|ribs?|tenderloin/i, '/proteins/pork_1.png'],
];

function getProteinImage(meal: { name?: string; ingredients?: { name: string; on_sale?: boolean }[]; easy_night?: boolean }): string | null {
  // Check the on_sale protein ingredient first (most accurate)
  const saleIngredient = meal.ingredients?.find(i => i.on_sale)?.name ?? '';
  const mealName = meal.name ?? '';
  const searchText = `${saleIngredient} ${mealName}`;
  for (const [re, img] of PROTEIN_IMAGES) {
    if (re.test(searchText)) return img;
  }
  return null;
}

// ── Meal type emoji ──────────────────────────────────────────────────────────

const TYPE_EMOJI: Record<string, string> = { breakfast: '🍳', lunch: '🥗', dinner: '🍽️', 'easy night': '⚡' };

function MealTypeIcon({ meal, easyNight }: { meal: { meal_type?: string; name?: string; photo_url?: string; ingredients?: { name: string; on_sale?: boolean }[] }; easyNight?: boolean }) {
  // ChefGPT photo takes priority
  if ((meal as any).photo_url) {
    return (
      <div className="w-12 h-12 rounded-xl overflow-hidden border border-border/40 shrink-0 bg-muted relative">
        <img src={(meal as any).photo_url} alt={meal.name} className="w-full h-full object-cover" loading="lazy"
          onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
      </div>
    );
  }
  const proteinImg = getProteinImage(meal);
  if (easyNight) {
    return (
      <div className="w-12 h-12 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-2xl shrink-0 overflow-hidden">
        ⚡
      </div>
    );
  }
  if (proteinImg) {
    return (
      <div className="w-12 h-12 rounded-xl overflow-hidden border border-border/40 shrink-0 bg-muted">
        <img src={proteinImg} alt="" className="w-full h-full object-cover" loading="lazy" />
      </div>
    );
  }
  const type = (meal.meal_type ?? '').toLowerCase().includes('breakfast') ? 'breakfast'
    : (meal.meal_type ?? '').toLowerCase().includes('lunch') ? 'lunch' : 'dinner';
  return (
    <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-2xl shrink-0">
      {TYPE_EMOJI[type]}
    </div>
  );
}

// ── Meal type tabs ────────────────────────────────────────────────────────────
type MealTab = 'all' | 'chef' | 'basics' | 'lazy_night' | 'bonus';
const TABS: { id: MealTab; label: string }[] = [
  { id: 'all',        label: '🍽️ All'         },
  { id: 'chef',       label: '👨‍🍳 Chef'        },
  { id: 'basics',     label: '🏠 Basics'       },
  { id: 'lazy_night', label: '😴 Lazy Night'   },
  { id: 'bonus',      label: '🎁 Bonus Pool'   },
];

function getMealType(meal: Partial<import('@/lib/index').Meal>): MealTab {
  // New category field (v7+)
  if (meal.category === 'chef') return 'chef';
  if (meal.category === 'basics') return 'basics';
  if (meal.category === 'lazy_night') return 'lazy_night';
  // Bonus meals by day
  if ((meal as any).day === 'Bonus') return 'bonus';
  // Legacy fallback flags
  if ((meal as any).easy_night) return 'lazy_night';
  if ((meal as any).basic_meal) return 'basics';
  return 'basics';
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function MealPlanPage() {
  const { mealPlan, cartMeals, addToCart, removeFromCart, postalCode, reset, selectedStore } = useAppStore();
  const { saveRecipe, unsaveRecipe, isRecipeSaved } = useSyncedRecipes();
  const { fetchRecipeImages } = useChefGPT();
  const [mealRatings, setMealRatings] = useState<Record<string, 1 | -1>>({});
  const [swapTarget, setSwapTarget]   = useState<SwapTarget | null>(null);
  const [swapToast, setSwapToast]     = useState<string>('');
  const imagesTriggeredRef = useRef<string>('');

  // Fetch ChefGPT recipe images once when the meal plan loads
  useEffect(() => {
    if (!mealPlan?.meals?.length) return;
    const planKey = mealPlan.meals.map(m => m.id).join(',');
    if (imagesTriggeredRef.current === planKey) return; // already fetching for this plan
    imagesTriggeredRef.current = planKey;
    fetchRecipeImages(mealPlan.meals);
  }, [mealPlan?.meals, fetchRecipeImages]);

  async function rateMeal(meal: typeof mealPlan.meals[0], rating: 1 | -1) {
    const key = meal.id;
    if (mealRatings[key] === rating) return; // already rated this way
    setMealRatings(prev => ({ ...prev, [key]: rating }));
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      await fetch('https://pgjncthsfxsylmvkgibx.supabase.co/functions/v1/meal-plan', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBnam5jdGhzZnhzeWxtdmtnaWJ4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDMxMzMwMzEsImV4cCI6MjA1ODcwOTAzMX0.kgnb-6jNYUSyJG5tXBzg0f39h4sVFyerS6BRpuQzgHU`,
        },
        body: JSON.stringify({
          action: 'rate_meal',
          postalCode: postalCode || 'M5V2T6',
          userId: user.id,
          recipeId: meal.id,
          recipeName: meal.name,
          cuisine: meal.method ?? '',
          method: meal.method ?? '',
          perServing: meal.per_serving ?? 0,
          rating,
        }),
      });
    } catch { /* rating is fire-and-forget */ }
  }
  const navigate = useNavigate();
  const [tab, setTab] = useState<MealTab>('all');

  if (!mealPlan) { navigate(ROUTE_PATHS.HOME); return null; }

  const allMeals   = mealPlan.meals ?? [];
  const cartIds    = new Set(cartMeals.map(m => m.id));
  const cartCost   = cartMeals.reduce((s, m) => s + mealCostCalc(m), 0);
  // Savings: use server value if available, else estimate 30% vs regular price
  const totalPlanCost = allMeals.reduce((s, m) => s + mealCostCalc(m), 0);
  const savings    = mealPlan.savings_vs_regular > 0
    ? mealPlan.savings_vs_regular
    : Math.round(totalPlanCost * 0.30 * 100) / 100;

  const counts: Record<MealTab, number> = {
    all:        allMeals.length,
    chef:       allMeals.filter(m => getMealType(m) === 'chef').length,
    basics:     allMeals.filter(m => getMealType(m) === 'basics').length,
    lazy_night: allMeals.filter(m => getMealType(m) === 'lazy_night').length,
    bonus:      allMeals.filter(m => getMealType(m) === 'bonus').length,
  };
  // For "all" tab, show core meals first (not Bonus), then bonus at the bottom
  const coreMeals  = allMeals.filter(m => getMealType(m) !== 'bonus');
  const bonusMeals = allMeals.filter(m => getMealType(m) === 'bonus');
  const filtered   = tab === 'all' ? coreMeals : allMeals.filter(m => getMealType(m) === tab);

  const handleRefresh = () => {
    const postal = postalCode || 'M5V3A8';
    navigate(`${ROUTE_PATHS.LOADING}?postal=${encodeURIComponent(postal)}&merchant=${encodeURIComponent(mealPlan.store_name)}`);
  };

  return (
    <div className="flex flex-col bg-background overflow-hidden" style={{ paddingTop: '48px', height: '100dvh' }}>

      {/* Header — sticks just below the fixed AppHeader */}
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border/50">
        <div className="container mx-auto px-4 h-14 flex items-center gap-2">
          <button onClick={() => navigate(ROUTE_PATHS.STORES)}
            className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors group shrink-0">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
            <span className="text-xs font-medium hidden sm:inline">Stores</span>
          </button>
          <div className="flex-1 min-w-0 mx-2">
            <span className="font-bold text-foreground truncate text-sm">{mealPlan.store_name}</span>
          </div>
          <button onClick={handleRefresh}
            className="flex items-center gap-1.5 text-sm font-bold text-primary border border-primary/40 bg-primary/8 hover:bg-primary/15 active:scale-95 transition-all px-3 py-2 rounded-xl shrink-0">
            <RefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">New Recipes</span>
          </button>
          <button onClick={() => navigate(ROUTE_PATHS.GROCERY)}
            disabled={cartMeals.length === 0}
            className="flex items-center gap-1.5 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1.5 rounded-full hover:opacity-90 transition-opacity disabled:opacity-40 shrink-0">
            <ShoppingCart className="w-3.5 h-3.5" />
            {cartMeals.length > 0 ? `My List (${cartMeals.length})` : 'My List'}
          </button>
        </div>
      </header>

      {/* Quick-nav strip */}
      <div className="bg-muted/40 border-b border-border/50">
        <div className="container mx-auto max-w-2xl px-4">
          <div className="flex items-center gap-2 overflow-x-auto py-2 scrollbar-none">
            <button onClick={() => navigate(ROUTE_PATHS.FLYER)}
              className="flex items-center gap-1.5 text-xs font-semibold bg-orange-500/10 text-orange-400 hover:bg-orange-500/20 border border-orange-500/20 px-3 py-1.5 rounded-full transition-colors whitespace-nowrap shrink-0">
              <Tag className="w-3.5 h-3.5" /> This Week's Flyer
            </button>
            <button onClick={() => navigate(ROUTE_PATHS.CALENDAR)}
              className="flex items-center gap-1.5 text-xs font-semibold bg-primary/10 text-primary hover:bg-primary/20 px-3 py-1.5 rounded-full transition-colors whitespace-nowrap shrink-0">
              <CalendarDays className="w-3.5 h-3.5" /> Calendar
            </button>
            <button onClick={() => navigate(ROUTE_PATHS.SAVED_RECIPES)}
              className="flex items-center gap-1.5 text-xs font-semibold bg-primary/10 text-primary hover:bg-primary/20 px-3 py-1.5 rounded-full transition-colors whitespace-nowrap shrink-0">
              <Bookmark className="w-3.5 h-3.5" /> My Recipes
            </button>
            <button onClick={() => navigate(ROUTE_PATHS.MY_PANTRY)}
              className="flex items-center gap-1.5 text-xs font-semibold bg-primary/10 text-primary hover:bg-primary/20 px-3 py-1.5 rounded-full transition-colors whitespace-nowrap shrink-0">
              <ChefHat className="w-3.5 h-3.5" /> My Pantry
            </button>
            <button onClick={() => navigate(ROUTE_PATHS.REVIEWS)}
              className="flex items-center gap-1.5 text-xs font-semibold bg-muted hover:bg-muted/80 border border-border text-foreground px-3 py-1.5 rounded-full transition-colors whitespace-nowrap shrink-0">
              <Star className="w-3.5 h-3.5 text-amber-500" /> Reviews
            </button>
            <button onClick={() => navigate(ROUTE_PATHS.STORES)}
              className="flex items-center gap-1.5 text-xs font-semibold bg-muted hover:bg-muted/80 border border-border text-foreground px-3 py-1.5 rounded-full transition-colors whitespace-nowrap shrink-0">
              <Store className="w-3.5 h-3.5" /> Change Store
            </button>
            <button onClick={() => { reset(); navigate(ROUTE_PATHS.HOME); }}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap shrink-0 ml-1">
              <RotateCcw className="w-3 h-3" /> Start over
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto overscroll-contain">
      <div className="container mx-auto max-w-2xl px-4 py-4 pb-28">

        {/* Category info banners */}
        {tab === 'chef' && counts.chef > 0 && (
          <div className="mb-4 rounded-2xl border border-purple-500/30 bg-purple-500/8 px-4 py-3 flex items-start gap-3">
            <span className="text-2xl leading-none mt-0.5">👨‍🍳</span>
            <div>
              <p className="font-bold text-foreground text-sm">Chef Meals</p>
              <p className="text-xs text-muted-foreground mt-0.5">Elevated weekend-worthy dinners — 45–60 min. Glazes, herb crusts, cheese bakes. Worth the extra effort.</p>
            </div>
          </div>
        )}
        {tab === 'basics' && counts.basics > 0 && (
          <div className="mb-4 rounded-2xl border border-blue-500/30 bg-blue-500/8 px-4 py-3 flex items-start gap-3">
            <span className="text-2xl leading-none mt-0.5">🏠</span>
            <div>
              <p className="font-bold text-foreground text-sm">Basics Every Night</p>
              <p className="text-xs text-muted-foreground mt-0.5">Simple, hearty weeknight meals in 25–40 min. One easy flavour upgrade — everyone eats, no stress.</p>
            </div>
          </div>
        )}
        {tab === 'lazy_night' && counts.lazy_night > 0 && (
          <div className="mb-4 rounded-2xl border border-amber-500/30 bg-amber-500/8 px-4 py-3 flex items-start gap-3">
            <span className="text-2xl leading-none mt-0.5">😴</span>
            <div>
              <p className="font-bold text-foreground text-sm">Lazy Night Meals</p>
              <p className="text-xs text-muted-foreground mt-0.5">Under 25 min, minimal effort. Frozen or pre-made items with a quick homemade touch. You've earned this.</p>
            </div>
          </div>
        )}
        {tab === 'bonus' && counts.bonus > 0 && (
          <div className="mb-4 rounded-2xl border border-green-500/30 bg-green-500/8 px-4 py-3 flex items-start gap-3">
            <span className="text-2xl leading-none mt-0.5">🎁</span>
            <div>
              <p className="font-bold text-foreground text-sm">Bonus Pool</p>
              <p className="text-xs text-muted-foreground mt-0.5">Extra recipes for the week — swap any core meal with one of these or use them when you need a break from the plan.</p>
            </div>
          </div>
        )}

        {/* Demo banner hidden from public */}

        {/* 💰 Savings banner — always visible */}
        {savings > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            className="mb-4 bg-green-500/10 border border-green-500/25 rounded-2xl px-4 py-3 flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-xl bg-green-500/20 border border-green-500/30 flex items-center justify-center shrink-0">
              <TrendingDown className="w-5 h-5 text-green-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-black text-green-500">
                You're saving ~${savings.toFixed(2)} this week
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                vs. buying the same meals at regular price · {mealPlan.sale_items_found} deals used
              </p>
            </div>
          </motion.div>
        )}

        {/* Cart summary bar */}
        <AnimatePresence>
          {cartMeals.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              className="mb-4 bg-primary/8 border border-primary/20 rounded-2xl px-4 py-3 flex items-center gap-3"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-foreground">
                  {cartMeals.length} meal{cartMeals.length !== 1 ? 's' : ''} selected
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Est. ${cartCost.toFixed(2)} · {cartMeals.reduce((s, m) => s + (sp(m.serves) || 4), 0)} servings
                </p>
              </div>
              <button onClick={() => navigate(ROUTE_PATHS.GROCERY)}
                className="flex items-center gap-1.5 bg-primary text-primary-foreground text-xs font-bold px-3 py-2 rounded-full hover:opacity-90 shrink-0">
                <ShoppingCart className="w-3.5 h-3.5" /> View List
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Meal-type tabs */}
        <div className="flex gap-2 mb-4 overflow-x-auto scrollbar-none">
          {TABS.map(t => {
            const cnt = counts[t.id];
            if (cnt === 0 && t.id !== 'all') return null;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap shrink-0 transition-colors ${
                  tab === t.id
                    ? 'bg-primary text-primary-foreground shadow-md shadow-primary/20'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}>
                {t.label}
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ml-0.5 ${
                  tab === t.id ? 'bg-white/20' : 'bg-background'
                }`}>{cnt}</span>
              </button>
            );
          })}
        </div>

        {/* Meal cards */}
        <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-3">
          <AnimatePresence mode="wait">
            {filtered.map(meal => {
              const inCart = cartIds.has(meal.id);
              const cost   = mealCostCalc(meal);
              const serves = sp(meal.serves) || 4;
              const mtype  = getMealType(meal);

              return (
                <motion.div key={meal.id} variants={staggerItem} layout
                  className={`bg-card border rounded-2xl overflow-hidden transition-all ${
                    mtype === 'chef'
                      ? 'border-purple-500/40 shadow-sm shadow-purple-500/10'
                      : mtype === 'lazy_night'
                        ? 'border-amber-500/40 shadow-sm shadow-amber-500/10'
                        : mtype === 'bonus'
                          ? 'border-green-500/40 shadow-sm shadow-green-500/10'
                          : inCart ? 'border-primary/40 shadow-md shadow-primary/10' : 'border-border'
                  }`}>
                  <div className="p-3">
                    <div className="flex items-stretch gap-3">

                      {/* Meal type icon */}
                      <MealTypeIcon meal={meal} easyNight={mtype === 'lazy_night'} />

                      {/* Info */}
                      <div className="flex-1 min-w-0 flex flex-col justify-between"
                        onClick={() => navigate(`/recipe/${meal.id}`)} role="button">
                        <div>
                          <div className="flex items-center gap-1.5 mb-0.5">
                            {mtype === 'chef' ? (
                              <span className="text-[10px] font-bold text-purple-400 uppercase tracking-wider">👨‍🍳 Chef</span>
                            ) : mtype === 'lazy_night' ? (
                              <span className="text-[10px] font-bold text-amber-500 uppercase tracking-wider">😴 Lazy Night</span>
                            ) : mtype === 'bonus' ? (
                              <span className="text-[10px] font-bold text-green-500 uppercase tracking-wider">🎁 Bonus</span>
                            ) : (
                              <span className="text-[10px] font-bold text-blue-400 uppercase tracking-wider">🏠 Basics</span>
                            )}
                          </div>
                          <h3 className="font-bold text-foreground text-sm leading-snug line-clamp-2">{meal.name}</h3>
                          {meal.tagline && (
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{meal.tagline}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="w-3 h-3" />{meal.cook_time ?? '30 min'}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Users className="w-3 h-3" />{serves}
                          </span>
                          {meal.nutrition?.calories && (
                            <span className="flex items-center gap-0.5 text-[10px] font-bold text-orange-400 bg-orange-500/10 border border-orange-500/20 px-1.5 py-0.5 rounded-full">
                              🔥 {meal.nutrition.calories} kcal
                            </span>
                          )}
                          {cost > 0 && (
                            <div className="ml-auto flex flex-col items-end">
                              <span className="text-base font-black text-primary leading-tight">
                                ${(cost / serves).toFixed(2)}<span className="text-xs font-bold">/srv</span>
                              </span>
                              <span className="text-[10px] text-muted-foreground font-medium leading-tight">
                                Total ${cost.toFixed(2)}
                              </span>
                            </div>
                          )}
                          {cost > 0 && (() => {
                            const lbl = budgetLabel(cost / serves);
                            const clr = budgetColor(lbl);
                            return <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${clr}`}>{lbl}</span>;
                          })()}
                        </div>
                        {/* Per-meal savings tag */}
                        {cost > 0 && (
                          <div className="flex items-center gap-1 mt-1">
                            <TrendingDown className="w-3 h-3 text-green-500 shrink-0" />
                            <span className="text-[10px] font-bold text-green-500">
                              ~${(cost * 0.30).toFixed(2)} saved vs. regular
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Checkbox + save + rate + chevron */}
                      <div className="flex flex-col items-center justify-between shrink-0 gap-2">
                        <button
                          onClick={() => inCart ? removeFromCart(meal.id) : addToCart(meal)}
                          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                            inCart ? 'bg-primary border-primary text-primary-foreground' : 'border-border hover:border-primary/50'
                          }`}>
                          {inCart && <Check className="w-3 h-3" strokeWidth={3} />}
                        </button>
                        {/* Save to My Recipes */}
                        {(() => {
                          const saved = isRecipeSaved(meal.id);
                          return (
                            <button
                              onClick={() => saved ? unsaveRecipe(meal.id) : saveRecipe(meal, selectedStore?.name)}
                              className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                                saved ? 'text-primary' : 'text-muted-foreground/40 hover:text-primary'
                              }`}
                              title={saved ? 'Remove from My Recipes' : 'Save to My Recipes'}
                            >
                              {saved ? <BookmarkCheck className="w-3.5 h-3.5" /> : <Bookmark className="w-3.5 h-3.5" />}
                            </button>
                          );
                        })()}
                        {/* 👍 / 👎 Rating */}
                        <button
                          onClick={() => rateMeal(meal, 1)}
                          title="I'd make this"
                          className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                            mealRatings[meal.id] === 1
                              ? 'text-green-500'
                              : 'text-muted-foreground/30 hover:text-green-500'
                          }`}>
                          <ThumbsUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => rateMeal(meal, -1)}
                          title="Not for me"
                          className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                            mealRatings[meal.id] === -1
                              ? 'text-red-400'
                              : 'text-muted-foreground/30 hover:text-red-400'
                          }`}>
                          <ThumbsDown className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => navigate(`/recipe/${meal.id}`)}
                          className="text-muted-foreground hover:text-primary">
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Sale badges */}
                    {meal.ingredients?.some(i => i.on_sale) && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {meal.ingredients.filter(i => i.on_sale).slice(0, 3).map((ing, idx) => (
                          <span key={idx} className="text-[10px] font-semibold bg-green-500/10 text-green-700 border border-green-500/20 px-2 py-0.5 rounded-full">
                            🏷 {ing.name} ${sp(ing.price).toFixed(2)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Footer action row */}
                  <div className="border-t border-border/50 px-4 py-2 flex items-center justify-between">
                    <button
                      onClick={() => inCart ? removeFromCart(meal.id) : addToCart(meal)}
                      className={`flex items-center gap-1.5 text-xs font-bold transition-colors ${
                        inCart ? 'text-destructive hover:text-destructive/80' : 'text-primary hover:text-primary/80'
                      }`}>
                      {inCart
                        ? <><span className="w-4 h-4 rounded-full bg-destructive/10 flex items-center justify-center">✕</span> Remove</>
                        : <><Plus className="w-4 h-4" /> Add to my list</>}
                    </button>
                    <div className="flex items-center gap-2">
                      {/* 🔀 Swap button */}
                      <button
                        onClick={() => setSwapTarget({ mealId: meal.id, mealObj: meal })}
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary font-semibold transition-colors"
                        title="Swap this recipe for another">
                        <Shuffle className="w-3.5 h-3.5" /> Generate New Meal
                      </button>
                      <FlagRecipeButton recipeName={meal.name} storeName={selectedStore?.name} trigger="icon" />
                      <button onClick={() => navigate(`/recipe/${meal.id}`)}
                        className="text-xs text-muted-foreground hover:text-foreground font-medium">
                        Full recipe →
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>

        {filtered.length === 0 && (
          <div className="text-center py-16">
            <p className="text-4xl mb-3">🍽️</p>
            <p className="text-sm font-semibold text-foreground mb-1">No {tab === 'lazy_night' ? 'Lazy Night' : tab} recipes yet</p>
            <p className="text-xs text-muted-foreground mb-4">Hit New Recipes to generate a fresh set</p>
            <button onClick={handleRefresh}
              className="flex items-center gap-2 bg-primary text-primary-foreground text-sm font-bold px-5 py-2.5 rounded-full hover:opacity-90 mx-auto">
              <RefreshCw className="w-4 h-4" /> New Recipes
            </button>
          </div>
        )}

        {/* Bonus Pool section — shown at bottom of "All" tab */}
        {tab === 'all' && bonusMeals.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">🎁</span>
              <h3 className="text-sm font-bold text-foreground">Bonus Pool</h3>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-500/10 text-green-600 border border-green-500/20">{bonusMeals.length} extras</span>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Swap any meal from your 7-night plan with one of these, or cook them when you need a break from the schedule.</p>
            <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-3">
              {bonusMeals.map(meal => {
                const inCart = cartIds.has(meal.id);
                const cost   = mealCostCalc(meal);
                const serves = sp(meal.serves) || 4;
                const mtype  = getMealType(meal);
                return (
                  <motion.div key={meal.id} variants={staggerItem}
                    className="bg-card border border-green-500/40 shadow-sm shadow-green-500/10 rounded-2xl overflow-hidden transition-all">
                    <div className="p-3">
                      <div className="flex items-stretch gap-3">
                        <MealTypeIcon meal={meal} easyNight={false} />
                        <div className="flex-1 min-w-0 flex flex-col justify-between"
                          onClick={() => navigate(`/recipe/${meal.id}`)} role="button">
                          <div>
                            <span className="text-[10px] font-bold text-green-500 uppercase tracking-wider">🎁 Bonus</span>
                            <h3 className="font-bold text-foreground text-sm leading-snug line-clamp-2">{meal.name}</h3>
                            {meal.tagline && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{meal.tagline}</p>}
                          </div>
                          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                            <span className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Clock className="w-3 h-3" />{meal.cook_time ?? '30 min'}
                            </span>
                            {meal.nutrition?.calories && (
                              <span className="flex items-center gap-0.5 text-[10px] font-bold text-orange-400 bg-orange-500/10 border border-orange-500/20 px-1.5 py-0.5 rounded-full">
                                🔥 {meal.nutrition.calories} kcal
                              </span>
                            )}
                            {cost > 0 && (
                              <span className="text-xs font-bold text-primary ml-auto">${(cost / serves).toFixed(2)}/srv</span>
                            )}
                            {cost > 0 && (() => {
                              const lbl = budgetLabel(cost / serves);
                              const clr = budgetColor(lbl);
                              return <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${clr}`}>{lbl}</span>;
                            })()}
                          </div>
                        </div>
                        <div className="flex flex-col items-center justify-between shrink-0 gap-2">
                          <button
                            onClick={() => inCart ? removeFromCart(meal.id) : addToCart(meal)}
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                              inCart ? 'bg-primary border-primary text-primary-foreground' : 'border-border hover:border-primary/50'
                            }`}>
                            {inCart && <Check className="w-3 h-3" strokeWidth={3} />}
                          </button>
                          <button onClick={() => navigate(`/recipe/${meal.id}`)} className="text-muted-foreground hover:text-primary">
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        )}

        <div className="h-8" />
      </div>
      </div>{/* end scrollable */}

      {/* ── Swap Meal Drawer ── */}
      <AnimatePresence>
        {swapTarget && (
          <SwapMealDrawer
            meal={swapTarget.mealObj}
            storeName={selectedStore?.name ?? 'your store'}
            onClose={() => setSwapTarget(null)}
            onSwapped={(newName) => {
              setSwapToast(`✅ Swapped to ${newName}`);
              setTimeout(() => setSwapToast(''), 3000);
            }}
          />
        )}
      </AnimatePresence>

      {/* ── Swap success toast ── */}
      <AnimatePresence>
        {swapToast && (
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 24 }}
            className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 bg-green-600 text-white text-sm font-bold px-5 py-3 rounded-2xl shadow-xl whitespace-nowrap"
          >
            {swapToast}
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}
